const BASE = '/assess/api';

export interface AcademyBranding {
  academy_id: string;
  name: string;
  tagline: string | null;
  logo_url: string | null;
  favicon_url: string | null;
  primary_color: string | null;
  allow_self_registration: boolean;
  /** Platform-admin gate for the CMS / website-builder feature. When false, the
   *  academy-admin SPA hides the Website tab. */
  website_builder_enabled: boolean;
}

export async function fetchBranding(slug: string): Promise<AcademyBranding> {
  const response = await fetch(`${BASE}/academy/branding`, {
    headers: { 'X-Academy-Slug': slug },
  });
  if (!response.ok) throw new Error('Academy not found');
  return response.json();
}

export async function fetchBrandingById(academyId: string): Promise<AcademyBranding> {
  const { apiClient } = await import('./client');
  return apiClient.get<AcademyBranding>(`/academy/branding/${academyId}`);
}

export async function updateBranding(data: {
  name?: string;
  tagline?: string;
  primary_color?: string;
}) {
  const { apiClient } = await import('./client');
  return apiClient.put('/academy/branding', data);
}

export async function uploadLogo(file: File) {
  const { apiClient } = await import('./client');
  const form = new FormData();
  form.append('file', file);
  return apiClient.upload('/academy/branding/logo', form);
}

export async function uploadFavicon(file: File) {
  const { apiClient } = await import('./client');
  const form = new FormData();
  form.append('file', file);
  return apiClient.upload('/academy/branding/favicon', form);
}

export async function updateSettings(data: { allow_self_registration: boolean }) {
  const { apiClient } = await import('./client');
  return apiClient.put('/academy/settings', data);
}

export interface ListMembersQuery {
  search?: string;
  role?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export interface ListMembersResponse {
  members: AcademyMember[];
  total: number;
  limit: number;
  offset: number;
}

export async function listMembers(query: ListMembersQuery = {}): Promise<ListMembersResponse> {
  const { apiClient } = await import('./client');
  const qs = new URLSearchParams();
  if (query.search) qs.set('search', query.search);
  if (query.role) qs.set('role', query.role);
  if (query.status) qs.set('status', query.status);
  if (query.limit != null) qs.set('limit', String(query.limit));
  if (query.offset != null) qs.set('offset', String(query.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiClient.get<ListMembersResponse>(`/academy/members${suffix}`);
}

export interface AcademyMember {
  id: string;
  user_id: string;
  display_name: string;
  email: string;
  role: string;
  status: string;
  invited_at: string;
}

export async function inviteMember(data: { email?: string; mobile?: string; role: string }) {
  const { apiClient } = await import('./client');
  return apiClient.post('/academy/members/invite', data);
}

export interface CreateMemberData {
  display_name: string;
  email: string;
  password: string;
  role: string;
}

export interface CreatedMember {
  user_id: string;
  email: string;
  display_name: string;
  role: string;
}

export async function createMember(data: CreateMemberData): Promise<CreatedMember> {
  const { apiClient } = await import('./client');
  return apiClient.post('/academy/members/create', data);
}

export async function removeMember(userId: string) {
  const { apiClient } = await import('./client');
  return apiClient.delete(`/academy/members/${userId}`);
}

export async function changeMemberRole(userId: string, role: string) {
  const { apiClient } = await import('./client');
  return apiClient.put(`/academy/members/${userId}/role`, { role });
}

export async function listInvitations() {
  const { apiClient } = await import('./client');
  return apiClient.get<{ invitations: AcademyInvitation[] }>('/academy/invitations');
}

export interface AcademyInvitation {
  id: string;
  email: string | null;
  mobile: string | null;
  role: string;
  expires_at: string;
}

export async function cancelInvitation(id: string) {
  const { apiClient } = await import('./client');
  return apiClient.delete(`/academy/invitations/${id}`);
}

export async function bulkInviteMembers(file: File): Promise<{ invited: number; errors: string[] }> {
  const { apiClient } = await import('./client');
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.upload<{ invited: number; errors: string[] }>('/academy/members/bulk-invite', formData);
}

export async function previewInvitation(token: string) {
  const response = await fetch(`${BASE}/invitations/${token}`);
  if (!response.ok) throw new Error('Invalid or expired invitation');
  return response.json() as Promise<{ academy_id: string; email: string | null; role: string; expires_at: string }>;
}

export async function acceptInvitation(token: string, data: { display_name: string; password: string }) {
  const response = await fetch(`${BASE}/invitations/${token}/accept`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Failed to join' }));
    throw new Error(err.error ?? 'Failed to join');
  }
  return response.json() as Promise<{ auth: { access_token: string; refresh_token: string } }>;
}

export async function academyRegister(
  slug: string,
  data: { display_name: string; email: string; password: string; mobile?: string }
) {
  const response = await fetch(`${BASE}/academy/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Academy-Slug': slug,
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Registration failed' }));
    throw new Error(err.error ?? 'Registration failed');
  }
  return response.json();
}
